import type { CompressedImage, MediaPreset } from "./types";

export async function compressImage(file: File, preset: MediaPreset): Promise<CompressedImage> {
	if (!file.type.startsWith("image/") || file.type === "image/gif") {
		return { file, width: 0, height: 0 };
	}
	if (typeof createImageBitmap !== "function") {
		return { file, width: 0, height: 0 };
	}

	try {
		const originalDims = await measureImage(file);

		const { default: imageCompression } = await import("browser-image-compression");
		const compressed = await imageCompression(file, {
			maxWidthOrHeight: preset.maxWidth,
			maxSizeMB: preset.maxSizeMB,
			fileType: preset.fileType,
			initialQuality: preset.initialQuality,
			useWebWorker: true,
		});

		if (compressed.size >= file.size) {
			return { file, ...originalDims };
		}

		const ext = preset.fileType === "image/jpeg" ? "jpg" : "webp";
		const stem = file.name.replace(/\.[^./]+$/, "");
		const renamed = new File([compressed], `${stem}.${ext}`, {
			type: preset.fileType,
			lastModified: Date.now(),
		});
		// Dimensions describe the file actually stored, so re-measure after resizing.
		const storedDims = await measureImage(renamed).catch(() => originalDims);
		return { file: renamed, ...storedDims };
	} catch {
		return { file, width: 0, height: 0 };
	}
}

export async function generateThumbnail(file: File, preset: MediaPreset): Promise<CompressedImage> {
	return compressImage(file, preset);
}

async function measureImage(file: File): Promise<{ width: number; height: number }> {
	const bitmap = await createImageBitmap(file);
	const dims = { width: bitmap.width, height: bitmap.height };
	bitmap.close();
	return dims;
}
